import React from "react";
import HeroBanner from "../sections/HeroBanner";
import PageBanner from "../sections/PageBanner";
import WholesalersBanner from "../sections/WholesalersBanner";
import CommunityBanner from "../sections/CommunityBanner";
import ImageTextBlock from "../sections/ImageTextBlock";
import RichText from "../sections/RichText";
import PolicySections from "../sections/PolicySections";
import FaqAccordion from "../sections/FaqAccordion";
import ImageScroller from "../sections/ImageScroller";
import EmbedFrame from "../sections/EmbedFrame";
import ServicesGrid from "../sections/ServicesGrid";
import TrustHighlights from "../sections/TrustHighlights";
import WhyChooseUs from "../sections/WhyChooseUs";
import OemSolutions from "../sections/OemSolutions";
import ActionCards from "../sections/ActionCards";
import Affiliations from "../sections/Affiliations";
import Testimonials from "../sections/Testimonials";
import SalesContact from "../sections/SalesContact";
import VideoCta from "../sections/VideoCta";
import YoutubeVideo from "../sections/YoutubeVideo";
import WholesaleAccount from "../sections/WholesaleAccount";
import ContactDetails from "../sections/ContactDetails";
import LocationsMap from "../sections/LocationsMap";
import OurLocations from "../sections/OurLocations";
import VerifiedInstaller from "../sections/VerifiedInstaller";
import VerifiedLocations from "../sections/VerifiedLocations";
import ProductListTitle from "../sections/ProductListTitle";
import ProductListing from "../sections/ProductListing";
import ProductDetail from "../sections/ProductDetail";
import GlobalSearch from "../sections/GlobalSearch";
import Wishlist from "../sections/Wishlist";
import Cart from "../sections/Cart";
import CartRecover from "../sections/CartRecover";
import Checkout from "../sections/Checkout";
import DraftOrder from "../sections/DraftOrder";
import PaymentSuccess from "../sections/PaymentSuccess";
import GuestOrder from "../sections/GuestOrder";
import AccountLayout from "../sections/AccountLayout";
import Profile from "../sections/Profile";
import MyOrders from "../sections/MyOrders";
import OrderDetail from "../sections/OrderDetail";
import MyAddresses from "../sections/MyAddresses";
import MyCreditPoints from "../sections/MyCreditPoints";
import ChangePassword from "../sections/ChangePassword";
import Login from "../sections/Login";
import SignUp from "../sections/SignUp";
import ForgotPassword from "../sections/ForgotPassword";
import PasswordReset from "../sections/PasswordReset";
import AuthRedirect from "../sections/AuthRedirect";
import BlogBanner from "../sections/BlogBanner";
import BlogList from "../sections/BlogList";
import BlogDetail from "../sections/BlogDetail";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import { legacyDataToSections, normalizeSectionTypes } from "./legacySections";

// Section `type` = admin form file (sections/<Type>.js) = customer component
// (components/sections/<Type>/<Type>.js).

export const SECTION_CATEGORIES = {
  BANNERS: "Banners",
  CONTENT: "Content",
  FEATURES: "Services & Features",
  TRUST: "Trust & Social Proof",
  MEDIA: "Media & Call to Action",
  CONTACT: "Contact & Locations",
  SHOP: "Shop",
  CART: "Cart & Checkout",
  ACCOUNT: "Account",
  AUTH: "Authentication",
  BLOG: "Blog",
  LAYOUT: "Header & Footer",
};

const internal = (url) => ({ type: "internal", url });

// Layout sections belong only to their page group (header, footer) and are
// the only sections offered there; `singleton` allows one per page.
export const LAYOUT_GROUPS = ["header", "footer"];

const def = (type, Form, category, name, description, extra = {}) => ({
  type,
  name,
  category,
  description,
  Form,
  canDelete: true,
  defaultData: Form.defaultData ? Form.defaultData() : {},
  ...extra,
});

const C = SECTION_CATEGORIES;

export const AUTOLOAD_SECTIONS = [
  def(
    "HeroBanner",
    HeroBanner,
    C.BANNERS,
    "Hero Banner",
    "Large top banner with title, description, layered background images and the vehicle selector.",
  ),
  def(
    "PageBanner",
    PageBanner,
    C.BANNERS,
    "Page Banner",
    "Outline heading, title and description beside an image (e.g. About Us).",
  ),
  def(
    "WholesalersBanner",
    WholesalersBanner,
    C.BANNERS,
    "Wholesalers Banner",
    "Outline heading, title and description beside an image, wholesaler style.",
  ),
  def(
    "CommunityBanner",
    CommunityBanner,
    C.BANNERS,
    "Community Banner",
    "Dark full-width banner with text and an image.",
  ),

  def(
    "ImageTextBlock",
    ImageTextBlock,
    C.CONTENT,
    "Image & Text Block",
    "Title and rich description beside an image.",
  ),
  def("RichText", RichText, C.CONTENT, "Rich Text", "Free-form rich text (Quill) content."),
  def(
    "PolicySections",
    PolicySections,
    C.CONTENT,
    "Policy Sections",
    "Legal / policy page: title, intro and headed rich-text sections with dividers.",
  ),
  def(
    "FaqAccordion",
    FaqAccordion,
    C.CONTENT,
    "FAQ Accordion",
    "Collapsible questions and answers.",
  ),
  def(
    "ImageScroller",
    ImageScroller,
    C.CONTENT,
    "Image Scroller",
    "Text beside two auto-scrolling image columns.",
  ),
  def(
    "EmbedFrame",
    EmbedFrame,
    C.CONTENT,
    "Embedded Page / Form",
    "Embeds an external page or form (e.g. product request).",
  ),

  def(
    "ServicesGrid",
    ServicesGrid,
    C.FEATURES,
    "Services Grid",
    "Service cards with image, name and link.",
  ),
  def(
    "TrustHighlights",
    TrustHighlights,
    C.FEATURES,
    "Trust Highlights",
    "Icon + title highlights in four columns.",
  ),
  def(
    "WhyChooseUs",
    WhyChooseUs,
    C.FEATURES,
    "Why Choose Us",
    "Reason cards with icon, title and optional description.",
  ),
  def(
    "OemSolutions",
    OemSolutions,
    C.FEATURES,
    "OEM Solutions",
    "Icon highlights beside a title and description.",
  ),
  def(
    "ActionCards",
    ActionCards,
    C.FEATURES,
    "Action Cards",
    "Wholesalers card with benefits and button, plus the build card with checklist and image.",
  ),

  def(
    "Affiliations",
    Affiliations,
    C.TRUST,
    "Affiliations",
    "Auto-scrolling partner / brand logos.",
  ),
  def(
    "Testimonials",
    Testimonials,
    C.TRUST,
    "Testimonials",
    "Customer reviews slider with photo, date, rating and text.",
  ),
  def(
    "SalesContact",
    SalesContact,
    C.TRUST,
    "Sales Contact",
    "Sales contact with message, phone and email, plus a sales team slider.",
  ),

  def(
    "VideoCta",
    VideoCta,
    C.MEDIA,
    "Video CTA",
    "Headline and sub headline beside a YouTube video.",
  ),
  def(
    "YoutubeVideo",
    YoutubeVideo,
    C.MEDIA,
    "YouTube Video",
    "Centered YouTube video in a tablet frame.",
  ),
  def(
    "WholesaleAccount",
    WholesaleAccount,
    C.MEDIA,
    "Wholesale Account",
    "Image, text and a button opening the wholesale account form.",
  ),

  def(
    "ContactDetails",
    ContactDetails,
    C.CONTACT,
    "Contact Details",
    "Sales and wholesale phone numbers with a contact button.",
  ),
  def(
    "LocationsMap",
    LocationsMap,
    C.CONTACT,
    "Locations Map",
    "Tabs of locations, each with a map image.",
  ),
  def(
    "OurLocations",
    OurLocations,
    C.CONTACT,
    "Our Locations",
    "Interactive US map with up to three locations.",
  ),
  def(
    "VerifiedInstaller",
    VerifiedInstaller,
    C.CONTACT,
    "Verified Installer",
    "Installer intro with checklists and customer results.",
  ),
  def(
    "VerifiedLocations",
    VerifiedLocations,
    C.CONTACT,
    "Verified Locations Map",
    "Google map of verified installers with search.",
  ),

  def(
    "ProductListTitle",
    ProductListTitle,
    C.SHOP,
    "Product List Title",
    "Heading of a product listing page.",
  ),
  def("ProductListing", ProductListing, C.SHOP, "Product Listing", "Product grid with filters."),
  def(
    "ProductDetail",
    ProductDetail,
    C.SHOP,
    "Product Detail",
    "Product page for /products/:slug.",
  ),
  def("GlobalSearch", GlobalSearch, C.SHOP, "Product Search", "Product search box."),
  def("Wishlist", Wishlist, C.SHOP, "Wishlist", "The customer's wishlist."),

  def("Cart", Cart, C.CART, "Cart", "Cart items and totals."),
  def(
    "CartRecover",
    CartRecover,
    C.CART,
    "Recovered Cart",
    "Restores a saved cart for /cart/:slug.",
  ),
  def("Checkout", Checkout, C.CART, "Checkout", "Checkout steps and payment."),
  def(
    "DraftOrder",
    DraftOrder,
    C.CART,
    "Draft Order",
    "Review and pay a draft order for /order/:slug.",
  ),
  def("PaymentSuccess", PaymentSuccess, C.CART, "Payment Success", "Payment confirmation."),
  def("GuestOrder", GuestOrder, C.CART, "Guest Order", "Order lookup for guests."),

  def(
    "AccountLayout",
    AccountLayout,
    C.ACCOUNT,
    "Account Layout",
    "Dashboard sidebar around this page's other sections.",
    { singleton: true },
  ),
  def("Profile", Profile, C.ACCOUNT, "Profile", "Customer profile form."),
  def("MyOrders", MyOrders, C.ACCOUNT, "My Orders", "Customer order list."),
  def(
    "OrderDetail",
    OrderDetail,
    C.ACCOUNT,
    "Order Detail",
    "Single order for /customer-dashboard/my-order/:id.",
  ),
  def("MyAddresses", MyAddresses, C.ACCOUNT, "My Addresses", "Customer address book."),
  def(
    "MyCreditPoints",
    MyCreditPoints,
    C.ACCOUNT,
    "My Credit Points",
    "Credit points balance and transaction history.",
  ),
  def("ChangePassword", ChangePassword, C.ACCOUNT, "Change Password", "Change password form."),

  def("Login", Login, C.AUTH, "Login", "Sign-in form."),
  def("SignUp", SignUp, C.AUTH, "Sign Up", "Sign-up form."),
  def("ForgotPassword", ForgotPassword, C.AUTH, "Forgot Password", "Forgot password form."),
  def("PasswordReset", PasswordReset, C.AUTH, "Password Reset", "Reset / create password form."),
  def("AuthRedirect", AuthRedirect, C.AUTH, "Auth Redirect", "Completes Auth0 sign-in."),

  def("BlogBanner", BlogBanner, C.BLOG, "Blog Banner", "Blog listing banner."),
  def("BlogList", BlogList, C.BLOG, "Blog List", "List of blog posts."),
  def(
    "BlogDetail",
    BlogDetail,
    C.BLOG,
    "Blog Article & Details",
    "Blog article: title, heading, content, excerpt, featured image, author, category, tags.",
    {
      defaultData: {
        title: "",
        heading: "",
        content: "",
        excerpt: "",
        feature_image: "",
        author: "",
        category: "",
        tags: [],
        reading_time: "",
        publish_date: null,
      },
    },
  ),

  def(
    "Header",
    Header,
    C.LAYOUT,
    "Header & Navigation",
    "Header: logo, product search, wishlist/cart, sign in/up buttons, navigation menu with dropdowns, and customer care phone.",
    {
      groups: ["header"],
      singleton: true,
      defaultData: {
        logo: [],
        logo_alt: "",
        logo_link: internal(""),
        search: {
          enabled: true,
          placeholders: [],
          no_result_text: "",
          request_link_text: "",
          request_link: internal(""),
        },
        actions: [],
        navigation: [],
        customer_care: { enabled: true, icon: "", label: "", phone: "" },
      },
    },
  ),
  def(
    "Footer",
    Footer,
    C.LAYOUT,
    "Footer",
    "Footer in 4 columns: logo/contact/social/payments, two link columns, happy-customer counter with badges; plus copyright and colors.",
    {
      groups: ["footer"],
      singleton: true,
      defaultData: {
        logo: [],
        logo_alt: "",
        contact: { address_label: "", address: "", phone_label: "", phone: "" },
        social_links: [],
        payments: { title: "", items: [] },
        column_2: { title: "", links: [] },
        column_3: { title: "", links: [] },
        stats: { count: "", suffix: "", title: "", description: "", badges: [] },
        copyright: "",
        style: { background_color: "", text_color: "", heading_color: "", link_color: "" },
      },
    },
  ),
];

export const SECTION_REGISTRY = AUTOLOAD_SECTIONS.map(({ Form, ...rest }) => rest);

// Sections offered in the library for a given page group.
export const isSectionAllowedInGroup = (section, group) => {
  const g = String(group || "page").toLowerCase();
  if (LAYOUT_GROUPS.includes(g)) return (section.groups || []).includes(g);
  return !section.groups;
};

export const getGroupDefaultSection = (group) =>
  AUTOLOAD_SECTIONS.find((sec) => (sec.groups || []).includes(String(group || "").toLowerCase())) ||
  null;

export const getSectionDefinition = (type) =>
  AUTOLOAD_SECTIONS.find((sec) => sec.type === type) || null;

export const canSectionBeDeleted = (section) => {
  if (section?.canDelete !== undefined) return Boolean(section.canDelete);
  const definition = getSectionDefinition(section?.type);
  return definition?.canDelete !== false;
};

export const renderAutoloadSectionForm = (section, onDataChange, form) => {
  const definition = getSectionDefinition(section.type);
  if (!definition) {
    return (
      <div style={{ padding: "8px 0", color: "#8c8c8c" }}>
        Unknown section type "{section.type}". It is not rendered on the site; remove it or replace
        it with a current section.
      </div>
    );
  }
  const FormComponent = definition.Form;
  return <FormComponent data={section.data} onChange={onDataChange} form={form} />;
};

// Sections of a page's language data. Old flat records (no `sections`) are
// converted with the same mapping the customer site uses.
export const convertLegacyDataToSections = (langData, slug, group) => {
  if (!langData) return [];
  if (Array.isArray(langData)) return normalizeSectionTypes(langData);
  if (Array.isArray(langData.sections) && langData.sections.length > 0) {
    return normalizeSectionTypes(langData.sections);
  }
  return legacyDataToSections(langData, { slug, group });
};
